import { Link } from 'react-router-dom';
import React, { useEffect, useState, useCallback } from 'react';
import { useApi } from '../../hooks/useApi';
import MismatchFlagModal from '../mismatch/MismatchFlagModal';
import PB101OutcomeModal from '../pb101/PB101OutcomeModal';
import { useClub } from '../../hooks/useClub';
import { useAuth } from '../../hooks/useAuth';
import { WinBackOfferScript, OfferSequencePanel, OFFER_SEQUENCE_SHOWN_FOR, BASE_PLAN_TYPES, DENTON_PLAN_TYPES, CLOSE_RESULTS, TIER_SOLD_OPTIONS_BASE, TIER_SOLD_OPTIONS_DEN } from './LeadOutcomeModal';

// ─── Assign Modal ─────────────────────────────────────────────────────────────
function AssignModal({ prospect, onClose, onAssigned }) {
  const { post } = useApi();
  const { user } = useAuth();
  const [saving, setSaving] = useState(false);

  async function handleSelfAssign() {
    setSaving(true);
    try {
      await post(`/lead/assign/${prospect.id}`, {
        assignToUserId: user.userId || user.sub,
        assignToName: user.name || user.email,
      });
      onAssigned();
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  }

  async function handleUnassign() {
    setSaving(true);
    try {
      await post(`/lead/unassign/${prospect.id}`, {});
      onAssigned();
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 380, width: '100%' }}>
        <div className="modal-title" style={{ textTransform: 'capitalize' }}>Assign Lead — {prospect.name}</div>
        <p style={{ fontSize: 13, color: 'var(--text)', marginBottom: 16, lineHeight: 1.5 }}>
          {prospect.assignedToName
            ? `Currently assigned to ${prospect.assignedToName}.`
            : 'This lead is unassigned.'}
        </p>
        <div className="modal-footer" style={{ flexDirection: 'column', gap: 8 }}>
          <button
            className="btn btn-primary"
            style={{ width: '100%' }}
            onClick={handleSelfAssign}
            disabled={saving}
          >
            {saving ? 'Saving...' : '👤 Assign to Me'}
          </button>
          {prospect.assignedToName && (
            <button
              className="btn btn-ghost"
              style={{ width: '100%', color: 'var(--red)', borderColor: 'var(--red)' }}
              onClick={handleUnassign}
              disabled={saving}
            >
              Remove Assignment
            </button>
          )}
          <button className="btn btn-ghost" style={{ width: '100%' }} onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
// ─── Pipeline config ──────────────────────────────────────────────────────────

const PIPELINE_CONFIG = {
  'Lead Engine': {
    contactMethod: 'TEXT FIRST',
    contactColor:  '#3b82f6',
    contactOrder:  ['Text', 'Call', 'Email'],
    expiryReason:  'Recency signal fades — activity drops off scoring window. After that they move to Win Back.',
    expiryWindow:  30,
    windowLabel:   'Recency window closes',
    scriptKey:     'lead_engine_initial',
  },
  'Win Back Non-Member': {
    contactMethod: 'TEXT FIRST',
    contactColor:  '#3b82f6',
    contactOrder:  ['Text', 'Call', 'Email'],
    expiryReason:  'Scoring window closes. Primary engagement hook weakens as inactivity compounds.',
    expiryWindow:  45,
    windowLabel:   'Scoring window closes',
    scriptKey:     'lead_winback_nonmember',
  },
  'Win Back Past Member': {
    contactMethod: 'CALL FIRST',
    contactColor:  '#f97316',
    contactOrder:  ['Call', 'Text', 'Email'],
    expiryReason:  'Membership lapse timing resets after this date. Best re-engagement window closes.',
    expiryWindow:  60,
    windowLabel:   'Re-engagement window closes',
    scriptKey:     'lead_winback_pastmember',
  },
  'Trial Ready': {
    contactMethod: 'CALL FIRST',
    contactColor:  '#f97316',
    contactOrder:  ['Call', 'Text', 'Email'],
    expiryReason:  'Trial readiness signal lasts 2–3 weeks before engagement drops off.',
    expiryWindow:  21,
    windowLabel:   'Trial readiness signal fades',
    scriptKey:     'lead_trial_ready',
  },
};

const DEFAULT_PIPELINE = {
  contactMethod: 'TEXT FIRST',
  contactColor:  '#3b82f6',
  contactOrder:  ['Text', 'Call', 'Email'],
  expiryReason:  'Contact window closes — reach out now to maximize conversion.',
  expiryWindow:  30,
  windowLabel:   'Contact window closes',
  scriptKey:     'lead_engine_initial',
};

// ─── Script interpolation ─────────────────────────────────────────────────────

function getRepName(role, userName, clubName) {
  if (role === 'fd') return `Picklr ${clubName || '[Club Name]'}`;
  if (userName) return userName;
  return '[Rep Name]';
}

function interpolateScript(body, memberName, repName, clubName, eventName, ambassadorName, userEmail) {
  if (!body) return body;
  return body
    .replace(/\[Name\]/gi, memberName?.split(' ')[0] || '[Name]')
    .replace(/\[FIRST NAME\]/gi, memberName?.split(' ')[0] || '[First Name]')
    .replace(/\[Rep Name\]/gi, repName || '[Rep Name]')
    .replace(/\[Club Name\]/gi, clubName || '[Club Name]')
    .replace(/\[CLUB NAME\]/gi, clubName || '[Club Name]')
    .replace(/\[EVENT NAME\]/gi, eventName || '[Event Name]')
    .replace(/\[AMBASSADOR NAME\]/gi, ambassadorName || '[Ambassador Name]')
    .replace(/\[YOUR EMAIL\]/gi, userEmail || 'joshua@playpicklr.com')
    .replace(/\[EVENT DETAILS\]/gi, '[Event Details]');
}

// ─── Tier config ──────────────────────────────────────────────────────────────

const TIER_CONFIG = {
  hot:  { label: '🔥 Hot',   color: '#ef4444', barColor: '#ef4444', priority: 1 },
  warm: { label: '🌡️ Warm', color: '#f97316', barColor: '#f97316', priority: 2 },
  cool: { label: '🧊 Cool',  color: '#3b82f6', barColor: '#3b82f6', priority: 3 },
  cold: { label: '❄️ Cold',  color: '#6b7280', barColor: '#6b7280', priority: 4 },
};

const SALES_STATUSES = [
  'Not Started',
  'Text Sent',
  'Called',
  'Booked',
  'Converted',
  'Replied',
  'Qualified',
  'Booked L2P',
  'Waiting Reply',
  'Nurture',
];

const STATUS_COLORS = {
  'Not Started':   '#6b7280',
  'Text Sent':     '#3b82f6',
  'Called':        '#f97316',
  'Replied':       '#22c55e',
  'Qualified':     '#22c55e',
  'Booked':        '#a855f7',
  'Booked L2P':    '#a855f7',
  'Converted':     '#22c55e',
  'Waiting Reply': '#eab308',
  'Nurture':       '#6b7280',
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getSequenceLabel(num) {
  if (!num || num === 1) return 'Initial Contact';
  if (num === 2) return 'Follow-Up 1';
  if (num === 3) return 'Follow-Up 2';
  return `Follow-Up ${num - 1}`;
}

function getNextActionBadge(contactCount) {
  if (!contactCount || contactCount === 0) return null;
  if (contactCount === 1) return { label: 'FOLLOW-UP 1 DUE', color: '#f97316' };
  if (contactCount === 2) return { label: 'FOLLOW-UP 2 DUE', color: '#ef4444' };
  return { label: `FOLLOW-UP ${contactCount} DUE`, color: '#ef4444' };
}

function normalizePipeline(pipeline) {
  if (!pipeline) return '';
  return pipeline
    .split('_')
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

function getPipelineCfg(pipeline) {
  const normalized = normalizePipeline(pipeline);
  return PIPELINE_CONFIG[normalized] || PIPELINE_CONFIG[pipeline] || DEFAULT_PIPELINE;
}

function formatTime12hr(timeStr) {
  if (!timeStr) return null;
  // Handles "09:00:00" or "09:00" from postgres TIME field
  const [hourStr, minStr] = timeStr.split(':');
  const hour = parseInt(hourStr, 10);
  const min = minStr || '00';
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const h = hour % 12 || 12;
  return `${h}:${min} ${ampm}`;
}

function getDaysUntilExpiry(prospect) {
  const cfg = getPipelineCfg(prospect.pipeline);
  if (!prospect.lastContact && !prospect.last_contact_date) return cfg.expiryWindow;
  const lastContact = new Date(prospect.lastContact || prospect.last_contact_date);
  const daysSince = Math.floor((new Date() - lastContact) / 86400000);
  return Math.max(0, cfg.expiryWindow - daysSince);
}

function groupByTier(contacts) {
  const groups = { hot: [], warm: [], cool: [], cold: [], unknown: [] };
  contacts.forEach(p => {
    const tier = p.tier?.toLowerCase();
    if (groups[tier] !== undefined) groups[tier].push(p);
    else groups.unknown.push(p);
  });
  return [
    { key: 'hot',     label: '🔥 High Priority',  subtitle: 'Score 70–100 · Contact first', items: groups.hot,     headerColor: '#ef4444' },
    { key: 'warm',    label: '🌡️ Medium Priority', subtitle: 'Score 45–69 · Work after Hot', items: groups.warm,    headerColor: '#f97316' },
    { key: 'cool',    label: '🧊 Low Priority',    subtitle: 'Score 20–44',                  items: groups.cool,    headerColor: '#3b82f6' },
    { key: 'cold',    label: '❄️ Cold',            subtitle: 'Score 0–19',                   items: groups.cold,    headerColor: '#6b7280' },
    { key: 'unknown', label: 'Unscored',           subtitle: '',                              items: groups.unknown, headerColor: '#6b7280' },
  ].filter(g => g.items.length > 0);
}

// ─── Score Bar ────────────────────────────────────────────────────────────────

export function ScoreBar({ score, tier }) {
  const pct = Math.min(100, Math.max(0, score || 0));
  const cfg = TIER_CONFIG[tier?.toLowerCase()] || { barColor: '#6b7280' };
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
      <div style={{
        flex: 1, height: 5, background: '#222', borderRadius: 3, overflow: 'hidden',
      }}>
        <div style={{
          width: `${pct}%`, height: '100%',
          background: cfg.barColor,
          borderRadius: 3,
          transition: 'width 0.3s ease',
          boxShadow: `0 0 6px ${cfg.barColor}60`,
        }} />
      </div>
      <span style={{
        fontSize: 13, fontWeight: 800, color: cfg.barColor,
        fontFamily: 'DM Mono, monospace', minWidth: 28, textAlign: 'right',
      }}>
        {score || 0}
      </span>
    </div>
  );
}

// ─── Contact Method Badge ─────────────────────────────────────────────────────

export function ContactMethodBadge({ pipeline }) {
  const cfg = getPipelineCfg(pipeline);
  return (
    <span style={{
      background: cfg.contactColor + '22',
      color: cfg.contactColor,
      border: `1px solid ${cfg.contactColor}`,
      borderRadius: 4, padding: '2px 7px',
      fontSize: 10, fontWeight: 700, letterSpacing: '0.04em',
      whiteSpace: 'nowrap',
    }}>
      {cfg.contactMethod}
    </span>
  );
}

// ─── Promo Badge ──────────────────────────────────────────────────────────────

function PromoBadge({ promoName }) {
  if (!promoName) return null;
  return (
    <span style={{
      background: '#d9770622',
      color: '#d97706',
      border: '1px solid #d97706',
      borderRadius: 4, padding: '2px 7px',
      fontSize: 10, fontWeight: 700, letterSpacing: '0.03em',
      whiteSpace: 'nowrap',
    }}>
      {promoName}
    </span>
  );
}

// ─── Pipeline Badge ───────────────────────────────────────────────────────────

function PipelineBadge({ pipeline }) {
  return (
    <span style={{
      background: '#ffffff11', color: '#aaa',
      border: '1px solid #333', borderRadius: 4,
      padding: '2px 6px', fontSize: 10, whiteSpace: 'nowrap',
    }}>
      {normalizePipeline(pipeline)}
    </span>
  );
}

// ─── Eligibility Badge (GAP-L3) ──────────────────────────────────────────────

function EligibilityBadge({ eligibility, reason }) {
  if (!eligibility) return null;
  const config = {
    eligible:     { label: 'ELIGIBLE',     bg: '#16a34a22', color: '#22c55e', border: '#22c55e' },
    review:       { label: 'REVIEW',       bg: '#d9770622', color: '#f97316', border: '#f97316' },
    not_eligible: { label: 'NOT ELIGIBLE', bg: '#6b728022', color: '#9ca3af', border: '#6b7280' },
  };
  const cfg = config[eligibility] || config.review;
  return (
    <span
      title={reason || ''}
      style={{
        background: cfg.bg,
        color: cfg.color,
        border: `1px solid ${cfg.border}`,
        borderRadius: 4, padding: '2px 7px',
        fontSize: 10, fontWeight: 700, letterSpacing: '0.04em',
        whiteSpace: 'nowrap', cursor: 'default',
      }}
    >
      {cfg.label}
    </span>
  );
}

// ─── Tier Badge ───────────────────────────────────────────────────────────────

export function TierBadge({ tier }) {
  const cfg = TIER_CONFIG[tier?.toLowerCase()];
  if (!cfg) return null;
  return (
    <span style={{
      background: cfg.color + '22', color: cfg.color,
      border: `1px solid ${cfg.color}`, borderRadius: 4,
      padding: '2px 6px', fontSize: 10, fontWeight: 700,
    }}>
      {cfg.label}
    </span>
  );
}

// ─── Expiry Block ─────────────────────────────────────────────────────────────

function ExpiryBlock({ prospect }) {
  const cfg = getPipelineCfg(prospect.pipeline);
  const daysLeft = getDaysUntilExpiry(prospect);
  const isUrgent = daysLeft <= 7;
  const color = isUrgent ? '#ef4444' : '#6b7280';

  return (
    <div style={{
      marginTop: 10, padding: '8px 10px',
      background: isUrgent ? '#1a0a0a' : '#111',
      border: `1px solid ${color}33`,
      borderRadius: 6,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
        {isUrgent && <span style={{ fontSize: 12 }}>⚠</span>}
        <span style={{ fontSize: 10, fontWeight: 700, color, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          {isUrgent
            ? `⚠ ${daysLeft} day${daysLeft !== 1 ? 's' : ''} left`
            : `${cfg.windowLabel} · ${daysLeft}d`}
        </span>
      </div>
      <div style={{ fontSize: 11, color: '#888', lineHeight: 1.4 }}>{cfg.expiryReason}</div>
      <div style={{ fontSize: 11, color: isUrgent ? '#ef4444' : '#aaa', marginTop: 4, fontWeight: isUrgent ? 700 : 400 }}>
        {isUrgent
          ? `Contact today or tomorrow — ${daysLeft} day${daysLeft !== 1 ? 's' : ''} left`
          : `Best window: contact within ${Math.min(daysLeft, 14)} days`
        }
      </div>
    </div>
  );
}

// ─── Script Panel (GAP-L7) ────────────────────────────────────────────────────

export function ScriptPanel({ prospect, scripts, role, userName, clubName, userEmail }) {
  const [copied, setCopied] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [overrideScript, setOverrideScript] = useState(null);
  const cfg = getPipelineCfg(prospect.pipeline);

  // Prefer activation-specific suggested script if available
  const hasActivationScript = !overrideScript && prospect.suggestedScript && prospect.activationName;
  const script = overrideScript
    ? overrideScript
    : hasActivationScript
      ? { ...prospect.suggestedScript, body: prospect.suggestedScript.body, contact_method_order: prospect.suggestedScript.contactMethodOrder }
      : scripts?.find(s => s.script_key === cfg.scriptKey)
        || scripts?.find(s => s.script_key?.startsWith('lead_'))
        || null;

  const repName = getRepName(role, userName, clubName);
  const firstName = prospect.name?.split(' ')[0] || '[Name]';
  const eventName = prospect.activationName || '';
  const ambassadorName = prospect.activationName || '';
  const openerLine = `Hey ${firstName}, this is ${repName} from Picklr ${clubName || '[Club Name]'}.`;

  const fullScript = hasActivationScript
    ? prospect.suggestedScript.body
    : script
      ? `${openerLine}\n\n${interpolateScript(script.body, prospect.name, repName, clubName, eventName, ambassadorName, userEmail)}`
      : openerLine;

  function handleCopy() {
    navigator.clipboard.writeText(fullScript).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  return (
    <div style={{
      marginTop: 10,
      background: '#0d1117',
      border: '1px solid #facc1540',
      borderRadius: 6,
      overflow: 'hidden',
    }}>
      {/* Script panel header */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '7px 12px',
        background: '#facc1510',
        borderBottom: '1px solid #facc1520',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 12 }}>📋</span>
          <span style={{
            fontSize: 11, fontWeight: 700, color: '#facc15',
            textTransform: 'uppercase', letterSpacing: '0.05em',
            fontFamily: 'DM Sans, sans-serif',
          }}>
            {hasActivationScript ? `⚡ ${prospect.activationName} · Script` : script ? script.name || 'Script' : 'Opener'}
          </span>
          {script && (
            <span style={{
              background: '#facc1520', color: '#facc15',
              border: '1px solid #facc1540', borderRadius: 3,
              padding: '1px 6px', fontSize: 9, fontWeight: 700,
              letterSpacing: '0.04em',
            }}>
              {normalizePipeline(prospect.pipeline)}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <button
            onClick={() => setPickerOpen(p => !p)}
            style={{
              background: pickerOpen ? '#facc1520' : '#ffffff10',
              color: pickerOpen ? '#facc15' : '#ddd',
              border: `1px solid ${pickerOpen ? '#facc1540' : '#555'}`,
              borderRadius: 4, padding: '3px 10px',
              fontSize: 10, fontWeight: 700, cursor: 'pointer',
              fontFamily: 'DM Sans, sans-serif',
            }}
          >
            ↕ Switch Script
          </button>
          <button
            onClick={handleCopy}
            style={{
              background: copied ? '#22c55e22' : '#facc1520',
              color: copied ? '#22c55e' : '#facc15',
              border: `1px solid ${copied ? '#22c55e' : '#facc1540'}`,
              borderRadius: 4, padding: '3px 10px',
              fontSize: 10, fontWeight: 700, cursor: 'pointer',
              fontFamily: 'DM Sans, sans-serif',
              transition: 'all 0.15s ease',
            }}
          >
            {copied ? '✓ Copied' : 'Copy'}
          </button>
        </div>
      </div>

      {pickerOpen && (
        <div style={{ borderBottom: '1px solid #1a1a1a' }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#666', textTransform: 'uppercase', letterSpacing: '0.06em', padding: '6px 12px', background: '#1a1a1a' }}>
            Lead Engine Scripts — tap to switch
          </div>
          {(scripts || []).map((s, i) => (
            <button key={s.script_id || i} onClick={() => { setOverrideScript(s); setPickerOpen(false); }} style={{
              width: '100%', textAlign: 'left', padding: '7px 12px',
              background: script && s.script_id === script.script_id ? 'rgba(250,204,21,0.1)' : '#111',
              border: 'none', borderBottom: i < (scripts || []).length - 1 ? '1px solid #1a1a1a' : 'none',
              cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#e5e5e5' }}>{s.name}</div>
                {s.timing && <div style={{ fontSize: 10, color: '#666', marginTop: 1 }}>{s.timing}</div>}
              </div>
              {script && s.script_id === script.script_id && (
                <span style={{ fontSize: 9, fontWeight: 700, color: '#facc15', background: 'rgba(234,179,8,0.15)', padding: '1px 5px', borderRadius: 3 }}>CURRENT</span>
              )}
            </button>
          ))}
        </div>
      )}

      {/* Opener line — always shown */}
      <div style={{
        padding: '8px 12px',
        borderBottom: script ? '1px solid #1a1a1a' : 'none',
      }}>
        <div style={{ fontSize: 10, color: '#555', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
          Opener
        </div>
        <div style={{ fontSize: 12, color: '#e5e5e5', lineHeight: 1.5, fontStyle: 'italic' }}>
          "{openerLine}"
        </div>
      </div>

      {/* Contact method order pills */}
      {script && script.contact_method_order && script.contact_method_order.length > 0 && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 6,
          padding: '6px 12px', borderBottom: '1px solid #1a1a1a',
          background: '#0a0e13',
        }}>
          <span style={{ fontSize: 10, color: '#555', textTransform: 'uppercase', letterSpacing: '0.04em', marginRight: 2 }}>Contact order:</span>
          {script.contact_method_order.map((method, i) => {
            const icons = { call: '📞', text: '💬', email: '📧' };
            const colors = { call: '#f97316', text: '#22c55e', email: '#3b82f6' };
            return (
              <span key={method} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                {i > 0 && <span style={{ color: '#333', fontSize: 10 }}>→</span>}
                <span style={{
                  fontSize: 10, fontWeight: 700,
                  color: colors[method] || '#888',
                  background: (colors[method] || '#888') + '15',
                  border: '1px solid ' + (colors[method] || '#888') + '40',
                  borderRadius: 3, padding: '1px 7px',
                  textTransform: 'uppercase', letterSpacing: '0.04em',
                }}>
                  {icons[method] || method} {method}
                </span>
              </span>
            );
          })}
        </div>
      )}

      {/* Script body */}
      {script && script.body && (
        <div style={{ padding: '8px 12px' }}>
          <div style={{ fontSize: 10, color: '#555', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            Script
          </div>
          <div style={{
            fontSize: 12, color: '#ccc', lineHeight: 1.6,
            whiteSpace: 'pre-wrap',
          }}>
            {hasActivationScript ? prospect.suggestedScript.body : interpolateScript(script.body, prospect.name, repName, clubName, eventName, ambassadorName)}
          </div>
          {(script.why_context || script.why_this_script) && (
            <div style={{
              marginTop: 8, padding: '5px 8px',
              background: '#ffffff08', borderRadius: 4,
              fontSize: 11, color: '#777', fontStyle: 'italic',
            }}>
              💡 {script.why_context || script.why_this_script}
            </div>
          )}
        </div>
      )}

      {/* No script found fallback */}
      {!script && (
        <div style={{ padding: '4px 12px 8px', fontSize: 11, color: '#555', fontStyle: 'italic' }}>
          No pipeline script seeded yet — use opener above. Add scripts in Admin → Scripts.
        </div>
      )}
    </div>
  );
}

// ─── LeadCard ─────────────────────────────────────────────────────────────────

function LeadCard({ prospect, expanded, onToggle, onLog, onView, onAssign, onStatusUpdate, statusUpdating, scripts, role, userName, userEmail, clubName, onFlag }) {
  const cfg = getPipelineCfg(prospect.pipeline);
  const daysLeft = getDaysUntilExpiry(prospect);
  const isUrgent = daysLeft <= 7;
  const [showScript, setShowScript] = useState(false);
  const [fullProspect, setFullProspect] = useState(null);
  const { get } = useApi();

  // Fetch full prospect detail when script panel opens (to get suggestedScript)
  useEffect(() => {
    if (showScript && prospect.activationId && !fullProspect && get) {
      get(`/lead/prospect/${prospect.id}`)
        .then(data => setFullProspect(data))
        .catch(() => {});
    }
  }, [showScript, prospect.activationId, prospect.id]); // eslint-disable-line

  // Reset script panel when card collapses
  useEffect(() => {
    if (!expanded) { setShowScript(false); setFullProspect(null); }
  }, [expanded]);

  return (
    <div
      style={{
        background: 'var(--surface-2, #111)',
        border: `1px solid ${isUrgent ? '#ef444440' : 'var(--border, #222)'}`,
        borderRadius: 8,
        marginBottom: 8,
        overflow: 'hidden',
        cursor: 'pointer',
      }}
      onClick={onToggle}
    >
      {/* Collapsed row */}
      <div style={{ padding: '10px 14px' }}>
        {/* Top row: name + badges */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap', marginBottom: 4 }}>
              <span style={{ fontWeight: 700, fontSize: 14, color: 'var(--text, #e5e5e5)', fontFamily: 'DM Sans, sans-serif', textTransform: 'capitalize' }}>
                {prospect.name}
              </span>
              <TierBadge tier={prospect.tier} />
              <PipelineBadge pipeline={prospect.pipeline} />
              <ContactMethodBadge pipeline={prospect.pipeline} />
              {prospect.activationName && (
                <span style={{
                  background: 'rgba(251,191,36,0.12)', color: '#f59e0b',
                  border: '1px solid rgba(251,191,36,0.3)',
                  borderRadius: 4, padding: '2px 7px',
                  fontSize: 10, fontWeight: 700, letterSpacing: '0.03em',
                }}>
                  ⚡ {prospect.activationName}
                </span>
              )}
              {/* BK, 9/24/26: distinct from the generic activation badge above —
                  that one requires reading the full campaign name to notice it's
                  a referral. Matches by name rather than a hardcoded activation_id
                  so it keeps working if the referral program ever runs under a
                  new activation for another market. */}
              {prospect.activationName && prospect.activationName.toLowerCase().includes('referral') && (
                <span style={{
                  background: 'rgba(56,189,248,0.12)', color: '#38bdf8',
                  border: '1px solid rgba(56,189,248,0.3)',
                  borderRadius: 4, padding: '2px 7px',
                  fontSize: 10, fontWeight: 700, letterSpacing: '0.03em',
                }}>
                  🎟️ Referral
                </span>
              )}
              <EligibilityBadge eligibility={prospect.trialEligibility} reason={prospect.eligibilityReason} />
              {prospect.activePromo && <PromoBadge promoName={prospect.activePromo} />}
              {isUrgent && (
                <span style={{ background: '#ef444422', color: '#ef4444', border: '1px solid #ef4444', borderRadius: 4, padding: '2px 6px', fontSize: 10, fontWeight: 700 }}>
                  ⚠ {daysLeft}d left
                </span>
              )}
              {!isUrgent && (() => {
                const badge = getNextActionBadge(prospect.contactCount);
                return badge ? (
                  <span style={{
                    background: badge.color + '22', color: badge.color,
                    border: `1px solid ${badge.color}`,
                    borderRadius: 4, padding: '2px 7px',
                    fontSize: 10, fontWeight: 700, letterSpacing: '0.04em',
                  }}>
                    {badge.label}
                  </span>
                ) : null;
              })()}
            </div>

            {/* Contact info */}
            <div style={{ fontSize: 11, color: '#777', marginBottom: 2 }}>
              {prospect.email}
              {prospect.phone && <span> · {prospect.phone}</span>}
            </div>

            {/* Insight strip — past trial + past member + 30d visits */}
            {(prospect.hadTrial || prospect.isPastMember || prospect.visits30d > 0) && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4, flexWrap: 'wrap' }}>
                {prospect.hadTrial && !prospect.trialConverted && (
                  <span style={{
                    background: 'rgba(168,85,247,0.10)',
                    color: '#c084fc',
                    border: '1px solid rgba(168,85,247,0.3)',
                    borderRadius: 4, padding: '2px 7px',
                    fontSize: 10, fontWeight: 700,
                  }}>
                    {(() => {
                      if (!prospect.trialEndDate) return '💜 Past Trial';
                      const days = Math.floor((new Date() - new Date(prospect.trialEndDate)) / 86400000);
                      return days <= 0 ? '💜 Past Trial' : `💜 Past Trial · ${days}d ago`;
                    })()}
                  </span>
                )}
                {prospect.isPastMember && (
                  <span style={{
                    background: 'rgba(59,130,246,0.10)',
                    color: '#60a5fa',
                    border: '1px solid rgba(59,130,246,0.3)',
                    borderRadius: 4, padding: '2px 7px',
                    fontSize: 10, fontWeight: 700,
                  }}>
                    {(() => {
                      if (!prospect.memberLapsedDate) return `💙 Past Member${prospect.membershipType ? ' · ' + prospect.membershipType : ''}`;
                      const days = Math.floor((new Date() - new Date(prospect.memberLapsedDate)) / 86400000);
                      return days <= 0 ? `💙 Past Member${prospect.membershipType ? ' · ' + prospect.membershipType : ''}` : `💙 Past Member · ${days}d ago`;
                    })()}
                  </span>
                )}
                {prospect.visits30d > 0 && (
                  <span style={{
                    background: prospect.visits30d >= 4 ? '#22c55e22' : prospect.visits30d >= 2 ? '#eab30822' : '#6b728022',
                    color: prospect.visits30d >= 4 ? '#22c55e' : prospect.visits30d >= 2 ? '#eab308' : '#9ca3af',
                    border: `1px solid ${prospect.visits30d >= 4 ? '#22c55e44' : prospect.visits30d >= 2 ? '#eab30844' : '#6b728044'}`,
                    borderRadius: 4, padding: '2px 7px',
                    fontSize: 10, fontWeight: 700,
                  }}>
                    {prospect.visits30d}x last 30d
                  </span>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Score bar — always visible */}
        <ScoreBar score={prospect.score} tier={prospect.tier} />

        {/* Follow-Up sequence lines — collapsed card */}
        {(prospect.followUp1 || prospect.followUp2 || (prospect.contactCount > 0)) && (
          <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: '6px 16px' }}>
            {prospect.followUp1 ? (
              <span style={{ fontSize: 11, color: '#f97316' }}>
                Follow-Up 1: <strong>{prospect.followUp1.outcome}</strong>
                {prospect.followUp1.date && <span style={{ color: '#888' }}> · {new Date(prospect.followUp1.date).toLocaleDateString()}</span>}
              </span>
            ) : prospect.contactCount >= 1 ? (
              <span style={{ fontSize: 11, color: '#f97316', fontWeight: 700 }}>⏳ Follow-Up 1 Due</span>
            ) : null}
            {prospect.followUp2 ? (
              <span style={{ fontSize: 11, color: '#ef4444' }}>
                Follow-Up 2: <strong>{prospect.followUp2.outcome}</strong>
                {prospect.followUp2.date && <span style={{ color: '#888' }}> · {new Date(prospect.followUp2.date).toLocaleDateString()}</span>}
              </span>
            ) : prospect.contactCount >= 2 ? (
              <span style={{ fontSize: 11, color: '#ef4444', fontWeight: 700 }}>⏳ Follow-Up 2 Due</span>
            ) : null}
          </div>
        )}
      </div>

      {/* Expanded section */}
      {expanded && (
        <div
          style={{ padding: '0 14px 14px', borderTop: '1px solid #1a1a1a' }}
          onClick={e => e.stopPropagation()}
        >
          {/* Detail grid */}
          <div style={{
            marginTop: 10, display: 'grid',
            gridTemplateColumns: '1fr 1fr', gap: '4px 16px',
            fontSize: 12, color: '#888',
          }}>
            {prospect.skillLevel && <span>🎯 {prospect.skillLevel}</span>}
            {prospect.duprRating && <span>DUPR {prospect.duprRating}</span>}
            {prospect.lastContact && (
              <span>Last contact: {new Date(prospect.lastContact || prospect.last_contact_date).toLocaleDateString()}</span>
            )}
            {prospect.score && <span>Score: <strong style={{ color: 'var(--text, #e5e5e5)' }}>{prospect.score}</strong></span>}
            {prospect.eligibilityReason && (
              <span style={{ gridColumn: 'span 2', color: '#777' }}>
                Trial eligibility: {prospect.eligibilityReason}
              </span>
            )}
            {prospect.contactCount > 0 && (
              <span style={{ gridColumn: 'span 2', color: '#aaa' }}>
                Contacts logged: <strong style={{ color: 'var(--text, #e5e5e5)' }}>{prospect.contactCount}</strong>
                {' '}· Next: <strong style={{ color: '#f97316' }}>{getSequenceLabel(prospect.contactCount + 1)}</strong>
              </span>
            )}
          </div>

          {/* Sales Status inline selector */}
          <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 10, color: '#666', textTransform: 'uppercase', letterSpacing: '0.05em', flexShrink: 0 }}>Sales Status</span>
            <select
              value={prospect.salesStatus || 'Not Started'}
              onChange={e => onStatusUpdate(prospect.id, e.target.value)}
              disabled={statusUpdating?.[prospect.id]}
              style={{
                background: (STATUS_COLORS[prospect.salesStatus || 'Not Started'] || '#6b7280') + '22',
                color: STATUS_COLORS[prospect.salesStatus || 'Not Started'] || '#6b7280',
                border: `1px solid ${STATUS_COLORS[prospect.salesStatus || 'Not Started'] || '#6b7280'}`,
                borderRadius: 5, padding: '3px 8px', fontSize: 11, fontWeight: 700,
                cursor: 'pointer', outline: 'none',
              }}
            >
              {SALES_STATUSES.map(s => (
                <option key={s} value={s} style={{ background: '#1a1a1a', color: '#e5e5e5' }}>{s}</option>
              ))}
            </select>
          </div>

          {/* Labeled contact sequence (expanded) */}
          {(prospect.followUp1 || prospect.followUp2) && (
            <div style={{ marginTop: 10, padding: '8px 10px', background: '#0d1117', border: '1px solid #1e2a3a', borderRadius: 6 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#555', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
                Sales Sequence
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <div style={{ fontSize: 11, color: '#888' }}>
                  <span style={{ color: '#666', minWidth: 110, display: 'inline-block' }}>Initial Contact</span>
                  <span style={{ color: '#aaa' }}>Logged</span>
                </div>
                {prospect.followUp1 && (
                  <div style={{ fontSize: 11, color: '#888' }}>
                    <span style={{ color: '#f97316', minWidth: 110, display: 'inline-block', fontWeight: 700 }}>Follow-Up 1</span>
                    <span style={{ color: '#e5e5e5' }}>{prospect.followUp1.outcome}</span>
                    {prospect.followUp1.date && <span style={{ color: '#555' }}> · {new Date(prospect.followUp1.date).toLocaleDateString()}</span>}
                  </div>
                )}
                {prospect.followUp2 && (
                  <div style={{ fontSize: 11, color: '#888' }}>
                    <span style={{ color: '#ef4444', minWidth: 110, display: 'inline-block', fontWeight: 700 }}>Follow-Up 2</span>
                    <span style={{ color: '#e5e5e5' }}>{prospect.followUp2.outcome}</span>
                    {prospect.followUp2.date && <span style={{ color: '#555' }}> · {new Date(prospect.followUp2.date).toLocaleDateString()}</span>}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Contact method guidance strip */}
          <div style={{
            marginTop: 10, padding: '6px 10px',
            background: cfg.contactColor + '11',
            border: `1px solid ${cfg.contactColor}33`,
            borderRadius: 6, fontSize: 11, color: cfg.contactColor,
            display: 'flex', alignItems: 'center', gap: 8,
          }}>
            <span style={{ fontWeight: 700 }}>{normalizePipeline(prospect.pipeline)}</span>
            <span style={{ color: '#666' }}>→</span>
            <span>{cfg.contactOrder.join(' → ')}</span>
          </div>

          {/* Expiry block */}
          <ExpiryBlock prospect={prospect} />

          {/* Script panel (GAP-L7) — shown when toggled */}
          {showScript && (
            <ScriptPanel prospect={fullProspect || prospect} scripts={scripts} role={role} userName={userName} userEmail={userEmail} clubName={clubName} />
          )}

          {/* Actions */}
          <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
            <button
              onClick={onLog}
              style={{
                background: '#facc15', color: '#000', border: 'none',
                borderRadius: 6, padding: '7px 18px', fontSize: 13,
                fontWeight: 700, cursor: 'pointer', fontFamily: 'DM Sans, sans-serif',
              }}
            >
              Log Contact
            </button>
            <button
              onClick={() => setShowScript(prev => !prev)}
              style={{
                background: showScript ? '#facc1520' : 'transparent',
                color: '#facc15',
                border: '1px solid #facc1540',
                borderRadius: 6, padding: '7px 14px',
                fontSize: 13, cursor: 'pointer',
                fontFamily: 'DM Sans, sans-serif',
                fontWeight: 600,
              }}
            >
              {showScript ? '▲ Hide Script' : '📋 View Script'}
            </button>
            <button
              onClick={onView}
              style={{
                background: 'transparent', color: '#aaa',
                border: '1px solid #333', borderRadius: 6,
                padding: '7px 14px', fontSize: 13, cursor: 'pointer',
              }}
            >
              View Record
            </button>
            <button
              onClick={onAssign}
              style={{
                background: prospect.assignedToName ? 'rgba(59,130,246,0.15)' : 'transparent',
                color: prospect.assignedToName ? '#60a5fa' : '#888',
                border: `1px solid ${prospect.assignedToName ? '#60a5fa' : '#333'}`,
                borderRadius: 6, padding: '7px 14px', fontSize: 13, cursor: 'pointer',
              }}
            >
              {prospect.assignedToName ? `👤 ${prospect.assignedToName}` : 'Assign'}
            </button>
            <button
              onClick={onToggle}
              style={{
                background: 'transparent', color: '#666',
                border: '1px solid #2a2a2a', borderRadius: 6,
                padding: '7px 14px', fontSize: 13, cursor: 'pointer',
              }}
            >
              Close
            </button>
            <button
              onClick={onFlag}
              style={{
                background: 'transparent', color: '#ef4444',
                border: '1px solid rgba(239,68,68,0.3)', borderRadius: 6,
                padding: '7px 14px', fontSize: 13, cursor: 'pointer',
              }}
              title="Flag as data mismatch"
            >
              🚩 Flag
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function LeadContacts({ onSelectProspect }) {
  const { get, post }    = useApi();
  const { currentClub }  = useClub();
  const { role, user }   = useAuth();
  const [data, setData]              = useState(null);
  const [loading, setLoading]        = useState(true);
  const [expandedId, setExpandedId]  = useState(null);
  const [outcomeTarget, setOutcomeTarget] = useState(null);
  const [mismatchTarget, setMismatchTarget] = useState(null);
  const [completed, setCompleted]    = useState([]);
  const [search, setSearch]          = useState('');
  const [scripts, setScripts]        = useState([]);
  const [assignTarget, setAssignTarget] = useState(null);
  const [statusUpdating, setStatusUpdating] = useState({});

  const load = useCallback(() => {
    setLoading(true);
    get(`/lead/contacts?club=${currentClub.id}`)
      .then(setData)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [currentClub.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load lead engine scripts once per club
  useEffect(() => {
    get('/scripts?module=lead_engine')
      .then(res => setScripts(res?.scripts || []))
      .catch(() => setScripts([]));
  }, [currentClub.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [currentClub.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return <div className="empty-state"><div className="spinner" /></div>;

  const allContacts  = data?.contacts || [];
  const completedIds = new Set(completed.map(c => c.id));
  const active       = allContacts.filter(p => !completedIds.has(p.id));

  const searchLower = search.toLowerCase();
  const isSearching = search.trim().length > 0;

  function matchesSearch(p) {
    return (
      p.name?.toLowerCase().includes(searchLower) ||
      p.email?.toLowerCase().includes(searchLower) ||
      p.phone?.includes(search) ||
      p.pipeline?.toLowerCase().includes(searchLower)
    );
  }

  function handleToggle(id) {
    setExpandedId(prev => prev === id ? null : id);
  }

  async function handleStatusUpdate(prospectId, newStatus) {
    setStatusUpdating(prev => ({ ...prev, [prospectId]: true }));
    try {
      await post(`/lead/status/${prospectId}`, { salesStatus: newStatus });
      setData(prev => prev ? ({
        ...prev,
        contacts: prev.contacts?.map(p =>
          p.id === prospectId ? { ...p, salesStatus: newStatus } : p
        ),
      }) : prev);
    } catch (e) {
      console.error('Status update failed', e);
    } finally {
      setStatusUpdating(prev => ({ ...prev, [prospectId]: false }));
    }
  }

  function handleOutcomeSaved(prospect, outcome) {
    setOutcomeTarget(null);
    setExpandedId(null);
    setCompleted(prev => [...prev.filter(c => c.id !== prospect.id), { ...prospect, outcome }]);
    load();
  }

  const displayContacts = isSearching ? active.filter(matchesSearch) : active;
  const tierGroups      = isSearching ? null : groupByTier(displayContacts);

  const hotCount  = allContacts.filter(p => p.tier === 'hot').length;
  const warmCount = allContacts.filter(p => p.tier === 'warm').length;

  return (
    <div>
      {/* Summary bar */}
      {allContacts.length > 0 && !isSearching && (
        <div style={styles.summaryBar}>
          <span>📋 <strong style={{ color: 'var(--text)' }}>{active.length}</strong> prioritized contacts · <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>{new Date().toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</span></span>
          {hotCount  > 0 && <span>🔥 <strong style={{ color: '#ef4444' }}>{hotCount}</strong> hot</span>}
          {warmCount > 0 && <span>🌡️ <strong style={{ color: '#f97316' }}>{warmCount}</strong> warm</span>}
        </div>
      )}

      {/* Search */}
      <div style={{ marginBottom: '1rem' }}>
        <input
          className="field-input"
          type="text"
          placeholder="Search by name, email, pipeline..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ width: '100%' }}
        />
        {isSearching && (
          <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 6 }}>
            Searching only today's contacts for {currentClub.name || currentClub.id}.{' '}
            <Link to={`/crm/search?q=${encodeURIComponent(search.trim())}`} style={{ color: 'var(--accent, #3b82f6)', fontWeight: 600 }}>
              Not here? Search everyone in every club →
            </Link>
          </div>
        )}
      </div>

      {/* Empty state */}
      {displayContacts.length === 0 && completed.length === 0 && (
        <div className="empty-state">
          <div className="empty-state-icon">📋</div>
          <div className="empty-state-text">
            {isSearching ? `No results for "${search}"` : 'No contacts scheduled for today.'}
          </div>
        </div>
      )}

      {/* Search results */}
      {isSearching && displayContacts.map(prospect => (
        <LeadCard
          key={prospect.id}
          prospect={prospect}
          expanded={expandedId === prospect.id}
          onToggle={() => handleToggle(prospect.id)}
          onLog={() => { setExpandedId(prospect.id); setOutcomeTarget(prospect); }}
          onView={() => onSelectProspect(prospect.id)}
          onFlag={() => setMismatchTarget(prospect)}
          onAssign={() => setAssignTarget(prospect)}
          onStatusUpdate={handleStatusUpdate}
          statusUpdating={statusUpdating}
          scripts={scripts}
          role={role}
          userName={user?.name}
          userEmail={user?.email}
          clubName={currentClub?.name}
        />
      ))}

      {/* Tier-grouped sections */}
      {!isSearching && tierGroups && tierGroups.map(group => (
        <div key={group.key} style={{ marginBottom: '1.5rem' }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8,
            marginBottom: 8, paddingBottom: 6,
            borderBottom: `1px solid ${group.headerColor}40`,
          }}>
            <span style={{
              fontFamily: 'DM Sans, sans-serif', fontWeight: 700, fontSize: 13,
              color: group.headerColor, letterSpacing: '0.05em', textTransform: 'uppercase',
            }}>
              {group.label}
            </span>
            <span style={{
              background: group.headerColor, color: '#000',
              borderRadius: 10, padding: '1px 8px', fontSize: 11, fontWeight: 700,
            }}>
              {group.items.length}
            </span>
            {group.subtitle && (
              <span style={{ fontSize: 11, color: '#666' }}>{group.subtitle}</span>
            )}
          </div>
          {group.items.map(prospect => (
            <LeadCard
              key={prospect.id}
              prospect={prospect}
              expanded={expandedId === prospect.id}
              onToggle={() => handleToggle(prospect.id)}
              onLog={() => { setExpandedId(prospect.id); setOutcomeTarget(prospect); }}
              onView={() => onSelectProspect(prospect.id)}
          onFlag={() => setMismatchTarget(prospect)}
              onAssign={() => setAssignTarget(prospect)}
              onStatusUpdate={handleStatusUpdate}
              statusUpdating={statusUpdating}
              scripts={scripts}
              role={role}
              userName={user?.name}
              userEmail={user?.email}
              clubName={currentClub?.name}
            />
          ))}
        </div>
      ))}

      {/* Completed */}
      {!isSearching && completed.length > 0 && (
        <div style={{ marginBottom: '1.5rem', opacity: 0.6 }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8,
            paddingBottom: 6, borderBottom: '1px solid #22c55e40',
          }}>
            <span style={{ fontWeight: 700, fontSize: 13, color: '#22c55e', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              ✅ Completed Today
            </span>
            <span style={{ background: '#22c55e', color: '#000', borderRadius: 10, padding: '1px 8px', fontSize: 11, fontWeight: 700 }}>
              {completed.length}
            </span>
          </div>
          {completed.map(prospect => (
            <div
              key={prospect.id}
              style={{
                background: 'var(--surface-2, #111)', border: '1px solid #1a2a1a',
                borderRadius: 8, padding: '10px 14px', marginBottom: 8,
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              }}
            >
              <div>
                <span style={{ fontWeight: 600, fontSize: 14, color: '#e5e5e5', fontFamily: 'DM Sans, sans-serif' }}>
                  {prospect.name}
                </span>
                <div style={{ marginTop: 4 }}>
                  <span style={{
                    background: '#22c55e22', color: '#22c55e',
                    border: '1px solid #22c55e', borderRadius: 4,
                    padding: '1px 6px', fontSize: 10, fontWeight: 700,
                  }}>
                    {prospect.outcome}
                  </span>
                </div>
              </div>
              <button
                style={{
                  background: '#22c55e22', color: '#22c55e',
                  border: '1px solid #22c55e', borderRadius: 6,
                  padding: '5px 12px', fontSize: 11, cursor: 'pointer',
                  fontFamily: 'DM Sans, sans-serif',
                }}
                onClick={() => setCompleted(prev => prev.filter(c => c.id !== prospect.id))}
              >
                ↩ They Responded
              </button>
            </div>
          ))}
        </div>
      )}

      {mismatchTarget && (
        <MismatchFlagModal
          module="lead"
          recordId={mismatchTarget.id}
          memberId={mismatchTarget.memberId}
          locationId={mismatchTarget.locationId || currentClub?.id}
          memberName={mismatchTarget.name}
          originalStatus={mismatchTarget.status}
          originalPipeline={mismatchTarget.pipeline}
          onClose={() => setMismatchTarget(null)}
          onFlagged={() => { setMismatchTarget(null); load(); }}
        />
      )}
      {outcomeTarget && (
        <LeadLogContactModal
          prospect={outcomeTarget}
          onClose={() => setOutcomeTarget(null)}
          onSaved={(outcome) => handleOutcomeSaved(outcomeTarget, outcome)}
          scripts={scripts}
          clubName={currentClub?.name}
        />
      )}
      {assignTarget && (
        <AssignModal
          prospect={assignTarget}
          onClose={() => setAssignTarget(null)}
          onAssigned={() => { setAssignTarget(null); load(); }}
        />
      )}
    </div>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const styles = {
  summaryBar: {
    display: 'flex', gap: '1rem', flexWrap: 'wrap',
    padding: '0.6rem 0.75rem',
    background: 'var(--surface-2)', border: '1px solid var(--border)',
    borderRadius: '8px', fontSize: '0.82rem',
    color: 'var(--text-secondary)', marginBottom: '1rem',
  },
};

// ─── Follow-Up Scheduler (embedded in Log Contact flow) ──────────────────────

const QUICK_PICKS = [
  { label: 'Today',      days: 0 },
  { label: 'Tomorrow',   days: 1 },
  { label: 'In 2 days',  days: 2 },
  { label: 'In 3 days',  days: 3 },
  { label: 'Next week',  days: 7 },
];

const TIME_OPTIONS = [
  '8:00 AM','8:30 AM','9:00 AM','9:30 AM','10:00 AM','10:30 AM',
  '11:00 AM','11:30 AM','12:00 PM','12:30 PM','1:00 PM','1:30 PM',
  '2:00 PM','2:30 PM','3:00 PM','3:30 PM','4:00 PM','4:30 PM',
  '5:00 PM','5:30 PM','6:00 PM','6:30 PM','7:00 PM','7:30 PM','8:00 PM',
];

function addDays(d, n) {
  const date = new Date(d);
  date.setDate(date.getDate() + n);
  return date;
}

function formatDateISO(date) {
  return date.toISOString().split('T')[0];
}

function formatDateDisplay(isoStr) {
  if (!isoStr) return '';
  // Strip any time component (handles both "2026-07-08" and "2026-07-08T00:00:00.000Z")
  const dateOnly = String(isoStr).split('T')[0];
  const d = new Date(dateOnly + 'T12:00:00');
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
}

// ─── Mini Calendar Component ─────────────────────────────────────────────────

function MiniCalendar({ selectedDate, minDate, onSelect }) {
  const today = new Date();
  const initDate = selectedDate ? new Date(selectedDate + 'T12:00:00') : today;
  const [viewYear, setViewYear]   = useState(initDate.getFullYear());
  const [viewMonth, setViewMonth] = useState(initDate.getMonth());

  const MONTHS = ['January','February','March','April','May','June',
                  'July','August','September','October','November','December'];
  const DAYS   = ['Su','Mo','Tu','We','Th','Fr','Sa'];

  function prevMonth() {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); }
    else setViewMonth(m => m - 1);
  }
  function nextMonth() {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); }
    else setViewMonth(m => m + 1);
  }

  const firstDay = new Date(viewYear, viewMonth, 1).getDay();
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const minISO = minDate || formatDateISO(today);

  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  return (
    <div style={{
      background: '#0d1117', border: '1px solid #333', borderRadius: 8,
      padding: 12, userSelect: 'none',
    }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <button onClick={prevMonth} style={{ background: 'none', border: 'none', color: '#888', fontSize: 16, cursor: 'pointer', padding: '0 6px' }}>‹</button>
        <span style={{ fontSize: 13, fontWeight: 700, color: '#e5e5e5' }}>{MONTHS[viewMonth]} {viewYear}</span>
        <button onClick={nextMonth} style={{ background: 'none', border: 'none', color: '#888', fontSize: 16, cursor: 'pointer', padding: '0 6px' }}>›</button>
      </div>
      {/* Day headers */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2, marginBottom: 4 }}>
        {DAYS.map(d => (
          <div key={d} style={{ textAlign: 'center', fontSize: 10, color: '#555', fontWeight: 700, padding: '2px 0' }}>{d}</div>
        ))}
      </div>
      {/* Date cells */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 }}>
        {cells.map((day, i) => {
          if (!day) return <div key={`e${i}`} />;
          const iso = `${viewYear}-${String(viewMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
          const isSelected = iso === selectedDate;
          const isToday    = iso === formatDateISO(today);
          const isPast     = iso < minISO;
          return (
            <button
              key={iso}
              disabled={isPast}
              onClick={() => !isPast && onSelect(iso)}
              style={{
                background: isSelected ? '#facc15' : isToday ? '#facc1520' : 'transparent',
                color: isSelected ? '#000' : isPast ? '#333' : isToday ? '#facc15' : '#ccc',
                border: isSelected ? 'none' : isToday ? '1px solid #facc1540' : '1px solid transparent',
                borderRadius: 5, padding: '5px 2px', fontSize: 12, cursor: isPast ? 'not-allowed' : 'pointer',
                fontWeight: isSelected ? 700 : 400, textAlign: 'center',
              }}
            >
              {day}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function FollowUpScheduler({ value, onChange }) {
  const [quickPick, setQuickPick]     = useState(null);
  const [customDate, setCustomDate]   = useState('');
  const [showCalendar, setShowCalendar] = useState(false);
  const [time, setTime]               = useState('9:00 AM');
  const [note, setNote]               = useState('');

  const today = new Date();

  function selectQuick(pick) {
    setQuickPick(pick.label);
    setShowCalendar(false);
    const date = formatDateISO(addDays(today, pick.days));
    setCustomDate(date);
    onChange({ date, time, note });
  }

  function handleCalendarSelect(iso) {
    setCustomDate(iso);
    setShowCalendar(false);
    onChange({ date: iso, time, note });
  }

  function handleTime(e) {
    setTime(e.target.value);
    onChange({ date: customDate, time: e.target.value, note });
  }

  function handleNote(e) {
    setNote(e.target.value);
    onChange({ date: customDate, time, note: e.target.value });
  }

  const selectedDate = customDate;

  return (
    <div>
      {/* Quick picks */}
      <div style={{ fontSize: 10, color: '#666', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
        Quick select
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
        {QUICK_PICKS.map(pick => (
          <button
            key={pick.label}
            onClick={() => selectQuick(pick)}
            style={{
              fontSize: 11, padding: '4px 10px', borderRadius: 4, cursor: 'pointer',
              border: `1px solid ${quickPick === pick.label ? '#facc15' : '#333'}`,
              background: quickPick === pick.label ? '#facc1515' : '#111',
              color: quickPick === pick.label ? '#facc15' : '#888',
              fontWeight: quickPick === pick.label ? 700 : 400,
            }}
          >
            {pick.label}
          </button>
        ))}
        <button
          onClick={() => { setQuickPick('custom'); setShowCalendar(prev => !prev); }}
          style={{
            fontSize: 11, padding: '4px 10px', borderRadius: 4, cursor: 'pointer',
            border: `1px solid ${quickPick === 'custom' ? '#facc15' : '#333'}`,
            background: quickPick === 'custom' ? '#facc1515' : '#111',
            color: quickPick === 'custom' ? '#facc15' : '#888',
          }}
        >
          📅 Pick date
        </button>
      </div>

      {/* Calendar — shown when Pick date selected */}
      {showCalendar && (
        <div style={{ marginBottom: 10 }}>
          <MiniCalendar
            selectedDate={selectedDate}
            minDate={formatDateISO(today)}
            onSelect={handleCalendarSelect}
          />
        </div>
      )}

      {/* Date + time row — shown once a date is selected */}
      {selectedDate && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 10, color: '#666', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>Date</div>
            <div
              onClick={() => { setQuickPick('custom'); setShowCalendar(prev => !prev); }}
              style={{
                background: '#111', border: '1px solid #facc1540', borderRadius: 5,
                padding: '6px 8px', fontSize: 12, color: '#facc15', cursor: 'pointer',
                fontWeight: 600,
              }}
            >
              📅 {formatDateDisplay(selectedDate)}
            </div>
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 10, color: '#666', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>Time</div>
            <select
              value={time}
              onChange={handleTime}
              style={{
                width: '100%', background: '#111', border: '1px solid #333',
                color: '#ccc', borderRadius: 5, padding: '6px 8px', fontSize: 12,
              }}
            >
              {TIME_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
        </div>
      )}

      {/* Follow-up note */}
      {selectedDate && (
        <div>
          <div style={{ fontSize: 10, color: '#666', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>Note for follow-up (optional)</div>
          <textarea
            value={note}
            onChange={handleNote}
            placeholder="e.g. Said she'd think about it — call back Thursday morning"
            style={{
              width: '100%', background: '#111', border: '1px solid #333',
              color: '#ccc', borderRadius: 5, padding: '6px 8px', fontSize: 12,
              resize: 'none', height: 52, fontFamily: 'inherit', boxSizing: 'border-box',
            }}
          />
        </div>
      )}
    </div>
  );
}

// ─── Log Contact Modal with Follow-Up Scheduling ─────────────────────────────

// Merged 10/6/26 (Josh: the Kanban's log-outcome had "limited options" vs the original Lead Engine).
// = the original LeadOutcomeModal list + this modal's contact-attempt/conversation outcomes.
const LEAD_OUTCOMES = [
  'Left Voicemail',
  'No Answer',
  'Text Sent',
  'Email Sent',
  'No Response',
  'Spoke With — Interested',
  'Spoke With — Soft No',
  'Spoke With — Hard No',
  'Engaged',
  'Follow-Up Needed',
  'Sold Trial',
  'Sold Membership',
  'Converted',
  'Re-Engage',
  'Not Interested — Cool Period',
  'Nurture',
  'Cool Period',
  'Not Interested — Do Not Contact',
  'Do Not Contact',
  'Closed',
];

const OUTCOMES_REQUIRING_WHY = ['Spoke With — Soft No', 'Spoke With — Hard No'];
const OUTCOMES_NO_FOLLOWUP   = ['Spoke With — Hard No', 'Not Interested — Do Not Contact', 'Do Not Contact'];
const OUTCOMES_SOLD          = ['Sold Trial', 'Sold Membership'];
const OUTCOMES_DNC           = ['Not Interested — Do Not Contact', 'Do Not Contact'];
const OUTCOMES_CONVERTED     = ['Converted', 'Sold Trial', 'Sold Membership'];

// queueInfo / onSkip are optional and only passed by the Lead Engine board's "work down the list" flow
// (LeadEngineKanban.jsx): a position label under the title and a Next button that moves on without logging.
export function LeadLogContactModal({ prospect, onClose, onSaved, scripts, clubName, queueInfo, onSkip }) {
  const { post, get } = useApi();
  const { user } = useAuth();
  const repName = (user?.name && !user.name.startsWith('auth0|')) ? user.name : (user?.email || 'Staff');

  const [outcome, setOutcome]       = useState('');
  const [why, setWhy]               = useState('');
  const [note, setNote]             = useState('');
  const [scheduleFollowUp, setScheduleFollowUp] = useState(false);
  const [followUp, setFollowUp]     = useState({ date: '', time: '9:00 AM', note: '' });
  const [saving, setSaving]         = useState(false);
  const [error, setError]           = useState(null);
  const [fullProspect, setFullProspect] = useState(null);
  const [contactMethods, setContactMethods] = useState([]);
  const [planType, setPlanType]       = useState('');
  const [tierSold, setTierSold]       = useState('');
  const [closeResult, setCloseResult] = useState('');
  const [offerSequence, setOfferSequence] = useState({
    offers: [{ made: false, type: '' }, { made: false, type: '' }, { made: false, type: '' }],
    discountUsed: false, discountAmount: '', promoOffered: false, termOffered: '', closeDate: '',
  });
  const isDenton = (prospect.locationId || prospect.location_id) === 'DEN';
  const PLAN_TYPES = isDenton ? DENTON_PLAN_TYPES : BASE_PLAN_TYPES;
  const TIER_SOLD_OPTIONS = isDenton ? TIER_SOLD_OPTIONS_DEN : TIER_SOLD_OPTIONS_BASE;

  function toggleContactMethod(method) {
    setContactMethods(prev =>
      prev.includes(method) ? prev.filter(m => m !== method) : [...prev, method]
    );
  }

  // ─── Sprint 19.7 — Log Note mode ─────────────────────────────────────────
  // Lets a rep log a freeform contact note without picking an outcome or
  // touching any existing follow-up. Writes to outcome_log only — see
  // logContactNote in queries.js. Independent of the outcome flow below.
  const [noteMode,          setNoteMode]          = useState(false);
  const [noteText,          setNoteText]          = useState('');
  const [noteContactMethods, setNoteContactMethods] = useState([]);
  const [noteSaving,        setNoteSaving]        = useState(false);
  const [noteError,         setNoteError]         = useState('');

  function toggleNoteContactMethod(method) {
    setNoteContactMethods(prev =>
      prev.includes(method) ? prev.filter(m => m !== method) : [...prev, method]
    );
  }

  async function handleSaveNote() {
    setNoteError('');
    if (!noteText.trim()) { setNoteError('Please enter a note.'); return; }
    setNoteSaving(true);
    try {
      await post('/contact/note', {
        module: 'lead',
        refId: prospect.id,
        notes: noteText,
        contactMethods: noteContactMethods,
      });
      onSaved(null);
    } catch (e) {
      setNoteError(e.message || 'Failed to save note.');
    } finally {
      setNoteSaving(false);
    }
  }

  // Fetch full prospect detail on mount — was activation-only (suggestedScript),
  // widened 9/26/26 to always fetch so the score breakdown (below) is always
  // available, not just for activation-sourced leads.
  useEffect(() => {
    if (get) {
      get(`/lead/prospect/${prospect.id}`)
        .then(data => setFullProspect(data))
        .catch(() => {});
    }
  }, [prospect.id]); // eslint-disable-line

  const needsWhy      = OUTCOMES_REQUIRING_WHY.includes(outcome);
  const noFollowUp    = OUTCOMES_NO_FOLLOWUP.includes(outcome);
  const isConverted   = OUTCOMES_CONVERTED.includes(outcome);
  const isSold        = OUTCOMES_SOLD.includes(outcome);
  const isDNC         = OUTCOMES_DNC.includes(outcome);
  const showOffers    = OFFER_SEQUENCE_SHOWN_FOR.includes(outcome);

  // Auto-toggle follow-up for converted (retention check-in)
  useEffect(() => {
    if (isConverted && !noFollowUp) setScheduleFollowUp(true);
    if (noFollowUp) setScheduleFollowUp(false);
  }, [outcome, isConverted, noFollowUp]);

  async function handleSave() {
    if (!outcome) { setError('Select an outcome'); return; }
    if (isSold && !planType) { setError('Select the plan type sold'); return; }
    if (needsWhy && !why.trim()) { setError('Please enter a reason'); return; }
    if (scheduleFollowUp && !followUp.date) { setError('Select a follow-up date'); return; }

    setSaving(true);
    setError(null);
    try {
      await post('/lead/followup', {
        actionId:         prospect.id,
        memberId:         prospect.memberId || prospect.member_id,
        locationId:       prospect.locationId || prospect.location_id,
        pipeline:         prospect.pipeline,
        outcome,
        note:             why ? `${note}
Reason: ${why}`.trim() : note,
        contactMethod:    contactMethods.length > 0 ? contactMethods[0] : null,
        contactMethods:   contactMethods.length > 0 ? contactMethods : undefined,
        followUpDate:     (scheduleFollowUp || !!followUp.date) ? followUp.date : null,
        followUpTime:     (scheduleFollowUp || !!followUp.date) ? followUp.time : null,
        followUpNote:     (scheduleFollowUp || !!followUp.date) ? followUp.note : null,
        // Plan sold / tier / close result / offer sequence (stored on the outcome row as JSON)
        details: (() => {
          const d = {};
          if (isSold && planType) d.planType = planType;
          if (isSold && tierSold) d.tierSold = tierSold;
          if (showOffers && closeResult) d.closeResult = closeResult;
          if (showOffers && (offerSequence.offers.some(o => o.made) || offerSequence.discountUsed
              || offerSequence.promoOffered || offerSequence.termOffered || offerSequence.closeDate)) d.offerSequence = offerSequence;
          return Object.keys(d).length ? d : undefined;
        })(),
      });
      onSaved(outcome);
    } catch (e) {
      setError(e.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ maxWidth: 480, width: '100%', maxHeight: '90vh', overflowY: 'auto', background: 'var(--surface-1, #111)', borderRadius: 10, padding: '20px 24px', boxShadow: '0 8px 32px rgba(0,0,0,0.5)' }}>
        <div className="modal-title">
          📝 Log Contact — <span style={{ color: '#facc15' }}>{prospect.name}</span>
        </div>
        <div style={{ fontSize: 11, color: '#666', marginBottom: 6 }}>
          {prospect.email} · Score {prospect.score} · {normalizePipeline(prospect.pipeline)}
        </div>
        {queueInfo && (
          <div style={{ fontSize: 11, color: '#60a5fa', marginBottom: 8 }}>{queueInfo}</div>
        )}

        {/* Score breakdown (BK, 9/26/26: "state WHY the lead has the score
            it does"). Brief by design — one row of chips, not an expandable
            panel — computed live from fullProspect.scoreBreakdown
            (getLeadProspect, queries.js), the same real scoreMember()
            formula the pipeline itself uses, not a re-derived guess. */}
        {fullProspect?.scoreBreakdown && (
          <div style={{ marginBottom: 14 }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
              {fullProspect.scoreBreakdown.map(b => {
                const pct = b.max > 0 ? b.points / b.max : 0;
                const color = pct >= 0.9 ? '#22c55e' : pct >= 0.4 ? '#f59e0b' : '#6b7280';
                return (
                  <span
                    key={b.key}
                    title={b.detail}
                    style={{
                      fontSize: 10, padding: '2px 7px', borderRadius: 4,
                      background: `${color}18`, color, border: `1px solid ${color}55`,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {b.label} {b.points}/{b.max}
                  </span>
                );
              })}
            </div>
            {typeof fullProspect.liveScore === 'number' && fullProspect.liveScore !== prospect.score && (
              <div style={{ fontSize: 10.5, color: '#f59e0b', marginTop: 5 }}>
                ⚠ Score may be stale — recalculates to {fullProspect.liveScore} ({fullProspect.liveTier}) as of today
              </div>
            )}
          </div>
        )}

        {/* Sprint 19.7 — Log Note toggle */}
        <div style={{ marginBottom: 14 }}>
          <button
            onClick={() => setNoteMode(m => !m)}
            style={{
              fontSize: 12, padding: '5px 12px', borderRadius: 5, cursor: 'pointer',
              border: `1px solid ${noteMode ? '#facc15' : '#333'}`,
              background: noteMode ? '#facc1515' : '#111',
              color: noteMode ? '#facc15' : '#888',
              fontWeight: noteMode ? 700 : 400,
            }}
          >
            📝 {noteMode ? 'Back to Log Outcome' : "Just log a note (keep existing follow-up)"}
          </button>
        </div>

        {noteMode ? (
          <div>
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 10, color: '#666', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>Contact Method</div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {[
                  { value: 'call',      label: '📞 Call' },
                  { value: 'text',      label: '💬 Text' },
                  { value: 'email',     label: '📧 Email' },
                  { value: 'in-person', label: '🤝 In Person' },
                ].map(cm => {
                  const active = noteContactMethods.includes(cm.value);
                  return (
                    <button
                      key={cm.value}
                      onClick={() => toggleNoteContactMethod(cm.value)}
                      style={{
                        fontSize: 12, padding: '5px 12px', borderRadius: 5, cursor: 'pointer',
                        border: `1px solid ${active ? '#facc15' : '#333'}`,
                        background: active ? '#facc1515' : '#111',
                        color: active ? '#facc15' : '#888',
                        fontWeight: active ? 700 : 400,
                      }}
                    >
                      {cm.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 10, color: '#666', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>Note *</div>
              <textarea
                rows={4}
                placeholder="What happened, what did they say..."
                value={noteText}
                onChange={e => setNoteText(e.target.value)}
                style={{ width: '100%', background: '#111', border: '1px solid #333', color: '#ccc', borderRadius: 5, padding: '8px 10px', fontSize: 13, resize: 'none', boxSizing: 'border-box' }}
              />
              <div style={{ fontSize: 10, color: '#555', marginTop: 3 }}>
                Saved to member contact history · does not change status or follow-up
              </div>
            </div>

            {noteError && (
              <div style={{ color: '#ef4444', fontSize: '0.85rem', margin: '0 0 0.5rem', padding: '0.5rem 0.75rem', background: 'rgba(239,68,68,0.1)', borderRadius: '6px' }}>
                {noteError}
              </div>
            )}
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSaveNote} disabled={noteSaving}>
                {noteSaving ? 'Saving...' : 'Save Note'}
              </button>
            </div>
          </div>
        ) : (
        <>
        {/* Win Back offer script (Sprint 21.3, 9/29/26) — prospect (the raw
            Kanban card, not fullProspect) already carries discountEligible/
            winBackOfferEligible from getLeadEngineKanbanBoard. */}
        {(prospect.discountEligible || prospect.winBackOfferEligible) && (
          <WinBackOfferScript prospect={prospect} clubName={clubName} />
        )}

        {/* Script reference */}
        {scripts && (
          <ScriptPanel prospect={fullProspect || prospect} scripts={scripts} role={null} userName={repName} userEmail={user?.email} clubName={clubName || ''} />
        )}

        {/* Outcome pills */}
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 10, color: '#666', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>Outcome</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {LEAD_OUTCOMES.map(o => (
              <button
                key={o}
                onClick={() => setOutcome(o)}
                style={{
                  fontSize: 11, padding: '4px 10px', borderRadius: 4, cursor: 'pointer',
                  border: `1px solid ${outcome === o ? '#22c55e' : '#333'}`,
                  background: outcome === o ? '#22c55e15' : '#111',
                  color: outcome === o ? '#22c55e' : '#888',
                  fontWeight: outcome === o ? 700 : 400,
                }}
              >
                {o}
              </button>
            ))}
          </div>
        </div>

        {/* Why field — required for Soft No / Hard No */}
        {needsWhy && (
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 10, color: '#ef4444', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
              Why? (required)
            </div>
            <input
              type="text"
              value={why}
              onChange={e => setWhy(e.target.value)}
              placeholder={outcome.includes('Soft') ? 'e.g. Price, timing, not ready, travel...' : 'e.g. Not interested, DNC...'}
              style={{
                width: '100%', background: '#111', border: '1px solid #ef444466',
                color: '#ccc', borderRadius: 5, padding: '6px 8px', fontSize: 12,
                boxSizing: 'border-box',
              }}
            />
          </div>
        )}

        {isDNC && (
          <div style={{ padding: '8px 12px', background: 'rgba(220,38,38,0.1)', border: '1px solid #dc2626', borderRadius: 6, fontSize: 12, marginBottom: 14, color: '#e5e5e5' }}>
            ⚠️ <strong>Hard suppress</strong> — this person is removed from ALL pipelines and won't be contacted again.
          </div>
        )}

        {isSold && (
          <>
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 10, color: '#666', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>Plan Type (required)</div>
              <select className="field-select" value={planType} onChange={e => setPlanType(e.target.value)}>
                <option value="">Select plan...</option>
                {PLAN_TYPES.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 10, color: '#666', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>Tier Sold</div>
              <select className="field-select" value={tierSold} onChange={e => setTierSold(e.target.value)}>
                <option value="">Select tier...</option>
                {TIER_SOLD_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          </>
        )}

        {showOffers && (
          <>
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 10, color: '#666', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>Close Result</div>
              <select className="field-select" value={closeResult} onChange={e => setCloseResult(e.target.value)}>
                <option value="">Select result...</option>
                {CLOSE_RESULTS.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <OfferSequencePanel offerSequence={offerSequence} onChange={setOfferSequence} />
          </>
        )}

        {/* Contact Method */}
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 10, color: '#666', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
            Contact Method <span style={{ textTransform: 'none', fontWeight: 400 }}>(select all that apply)</span>
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {[
              { value: 'call',      label: '📞 Call' },
              { value: 'text',      label: '💬 Text' },
              { value: 'email',     label: '📧 Email' },
              { value: 'in-person', label: '🤝 In Person' },
            ].map(cm => {
              const active = contactMethods.includes(cm.value);
              return (
                <button
                  key={cm.value}
                  onClick={() => toggleContactMethod(cm.value)}
                  style={{
                    fontSize: 12, padding: '5px 12px', borderRadius: 5, cursor: 'pointer',
                    border: `1px solid ${active ? '#facc15' : '#333'}`,
                    background: active ? '#facc1515' : '#111',
                    color: active ? '#facc15' : '#666',
                    fontWeight: active ? 700 : 400,
                  }}
                >
                  {cm.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Note */}
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 10, color: '#666', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
            Note {needsWhy ? '(optional)' : '(optional)'}
          </div>
          <textarea
            value={note}
            onChange={e => setNote(e.target.value)}
            placeholder="Additional context for next rep..."
            style={{
              width: '100%', background: '#111', border: '1px solid #333',
              color: '#ccc', borderRadius: 5, padding: '6px 8px', fontSize: 12,
              resize: 'none', height: 52, fontFamily: 'inherit', boxSizing: 'border-box',
            }}
          />
        </div>

        {/* Follow-up toggle */}
        {!noFollowUp && (
          <div style={{ marginBottom: 14 }}>
            <div
              onClick={() => !isConverted && setScheduleFollowUp(p => !p)}
              style={{
                display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10,
                cursor: isConverted ? 'default' : 'pointer',
              }}
            >
              {/* Toggle switch */}
              <div style={{
                width: 36, height: 20, borderRadius: 10,
                background: scheduleFollowUp ? '#facc15' : '#333',
                position: 'relative', transition: 'background 0.2s', flexShrink: 0,
              }}>
                <div style={{
                  width: 16, height: 16, borderRadius: '50%',
                  background: scheduleFollowUp ? '#000' : '#666',
                  position: 'absolute', top: 2,
                  left: scheduleFollowUp ? 18 : 2,
                  transition: 'left 0.2s',
                }} />
              </div>
              <span style={{ fontSize: 13, color: scheduleFollowUp ? '#facc15' : '#888' }}>
                {isConverted ? '📅 Retention check-in scheduled' : 'Schedule a follow-up'}
              </span>
            </div>

            {scheduleFollowUp && (
              <div style={{
                background: '#0d1117', border: '1px solid #facc1530',
                borderRadius: 8, padding: '12px 14px',
              }}>
                <FollowUpScheduler
                  value={followUp}
                  onChange={(val) => {
                    setFollowUp(val);
                    if (val.date) setScheduleFollowUp(true);
                  }}
                />
              </div>
            )}
          </div>
        )}

        {error && (
          <div style={{ color: '#ef4444', fontSize: 12, marginBottom: 10 }}>{error}</div>
        )}

        <div className="modal-footer" style={{ gap: 8 }}>
          <button className="btn btn-ghost" onClick={onClose} disabled={saving}>Cancel</button>
          {onSkip && (
            <button className="btn btn-ghost" onClick={onSkip} disabled={saving} title="Go to the next card without logging anything">
              Next →
            </button>
          )}
          <button
            className="btn btn-primary"
            onClick={handleSave}
            disabled={saving || !outcome}
            style={{ minWidth: 180 }}
          >
            {saving ? 'Saving...' : scheduleFollowUp ? '📅 Save + Schedule Follow-Up' : '✓ Save Contact'}
          </button>
        </div>
        </>
        )}
      </div>
    </div>
  );
}

// ─── Follow-Up Tab ────────────────────────────────────────────────────────────

const URGENCY_CONFIG = {
  red:    { label: 'Due today', color: '#ef4444', icon: '🔴', bg: '#ef444408', border: '#ef444430', badgeBg: '#ef444420' },
  orange: { label: 'Tomorrow',  color: '#f97316', icon: '🟠', bg: '#f9731608', border: '#f9731630', badgeBg: '#f9731620' },
  blue:   { label: '3–7 days',  color: '#60a5fa', icon: '🔵', bg: '#60a5fa08', border: '#60a5fa30', badgeBg: '#60a5fa20' },
};

function getFollowUpPipelineBadge(pipeline) {
  switch (pipeline) {
    case 't2m':
      return { label: 'T2M', color: '#eab308', bg: 'rgba(234,179,8,0.12)', border: '1px solid rgba(234,179,8,0.3)' };
    case 'pb101':
      return { label: 'PB101', color: '#3b82f6', bg: 'rgba(59,130,246,0.12)', border: '1px solid rgba(59,130,246,0.3)' };
    case 'inbound_lead':
      return { label: 'INBOUND', color: '#a855f7', bg: 'rgba(168,85,247,0.12)', border: '1px solid rgba(168,85,247,0.3)' };
    default:
      return { label: 'LE', color: '#888', bg: '#ffffff10', border: '1px solid #333' };
  }
}

function FollowUpCard({ fu, onLog, onReschedule, onDismiss, onView }) {
  const urg = URGENCY_CONFIG[fu.urgency] || URGENCY_CONFIG.blue;
  const overdue = fu.daysOverdue > 0;
  const overdueBy = fu.daysOverdue;

  return (
    <div style={{
      background: urg.bg,
      border: `1px solid ${urg.border}`,
      borderLeft: `3px solid ${urg.color}`,
      borderRadius: 8, padding: '10px 14px', marginBottom: 8,
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
            <span style={{ fontWeight: 700, fontSize: 14, color: '#e5e5e5' }}>{fu.name}</span>
            {overdue && (
              <span style={{ background: '#ef444425', color: '#ef4444', border: '1px solid #ef4444', borderRadius: 4, padding: '2px 7px', fontSize: 10, fontWeight: 700 }}>
                ⚠ OVERDUE {overdueBy}d
              </span>
            )}
            {!overdue && fu.followUpDate && (
              <span style={{ background: urg.badgeBg, color: urg.color, border: `1px solid ${urg.color}44`, borderRadius: 4, padding: '2px 7px', fontSize: 10, fontWeight: 700 }}>
                {fu.urgency === 'red' ? 'TODAY' : fu.urgency === 'orange' ? 'TOMORROW' : formatDateDisplay(fu.followUpDate)}
                {fu.followUpTime ? ` · ${formatTime12hr(fu.followUpTime)}` : ''}
              </span>
            )}
            {(() => {
              const pb = getFollowUpPipelineBadge(fu.pipeline);
              return (
                <span style={{
                  background: pb.bg, color: pb.color, border: pb.border,
                  borderRadius: 4, padding: '2px 6px', fontSize: 9, fontWeight: 600
                }}>
                  {pb.label}
                </span>
              );
            })()}
            {/* Sprint 21 continuation — status-only indicator, no note
                content, no link into Mismatch Queue. Only ever set for
                fu.pipeline === 't2m' | 'pb101' (see backend query) — never
                appears on Lead Engine's own records. */}
            {fu.frozenFlag && (
              <span
                title="A club staff member flagged this record for a data issue — it's paused until reviewed."
                style={{
                  background: '#f9731625', color: '#f97316', border: '1px solid #f9731660',
                  borderRadius: 4, padding: '2px 7px', fontSize: 10, fontWeight: 700,
                }}
              >
                🚩 Frozen — pending review
              </span>
            )}
          </div>
          <div style={{ fontSize: 11, color: '#666', marginBottom: fu.followUpNote ? 6 : 0 }}>
            {fu.email}
            {fu.scheduledBy && <span> · Scheduled by {fu.scheduledBy}</span>}
          </div>
          {fu.followUpNote && (
            <div style={{ fontSize: 11, color: '#888', fontStyle: 'italic', background: '#111', borderRadius: 4, padding: '4px 8px' }}>
              "{fu.followUpNote}"
            </div>
          )}
        </div>
      </div>

      <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
        <button
          onClick={onLog}
          style={{
            background: '#facc15', color: '#000', border: 'none',
            borderRadius: 6, padding: '6px 16px', fontSize: 12,
            fontWeight: 700, cursor: 'pointer',
          }}
        >
          {fu.pipeline === 't2m' ? 'Open Trial' : 'Log Contact'}
        </button>
        <button
          onClick={onReschedule}
          style={{
            background: 'transparent', color: '#888', border: '1px solid #333',
            borderRadius: 6, padding: '6px 12px', fontSize: 12, cursor: 'pointer',
          }}
        >
          Reschedule
        </button>
        <button
          onClick={onDismiss}
          style={{
            background: 'transparent', color: '#555', border: '1px solid #2a2a2a',
            borderRadius: 6, padding: '6px 12px', fontSize: 12, cursor: 'pointer',
          }}
        >
          Dismiss
        </button>
        {fu.pipeline !== 't2m' && onView && (
          <button
            onClick={onView}
            style={{
              background: 'transparent', color: '#60a5fa', border: '1px solid #60a5fa44',
              borderRadius: 6, padding: '6px 12px', fontSize: 12, cursor: 'pointer',
            }}
          >
            View Record
          </button>
        )}
      </div>
    </div>
  );
}

export function LeadFollowUps({ onSelectProspect, tab = 'all' }) {
  const { get, post } = useApi();
  const { currentClub } = useClub();
  const { role } = useAuth();
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [logTarget, setLogTarget]   = useState(null);
  const [reschedTarget, setReschedTarget] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    const clubParam = (role === 'admin' || role === 'mloc') ? 'ALL' : currentClub.id;
    get(`/lead/followups?club=${clubParam}&tab=${tab}`)
      .then(setData)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [currentClub.id, tab]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  async function handleDismiss(fu) {
    if (fu.pipeline === 't2m') {
      // Clear follow-up from trial_pipeline
      await post(`/t2m/followup/${fu.id}/dismiss`, {}).catch(console.error);
    } else if (fu.pipeline === 'pb101') {
      // Clear follow-up from outcome_log via the real ltp_id — NOT the actions table
      await post(`/pb101/followup/${fu.id}/dismiss`, {}).catch(console.error);
    } else {
      await post(`/lead/followup/${fu.id}/dismiss`, {}).catch(console.error);
    }
    load();
  }

  function handleLog(fu) {
    if (fu.pipeline === 't2m') {
      // Navigate to trial detail — onSelectProspect receives trial_id
      onSelectProspect && onSelectProspect(fu.id, 't2m');
    } else if (fu.pipeline === 'pb101') {
      // Opens PB101OutcomeModal (below) instead of the generic Lead Engine modal
      setLogTarget(fu);
    } else {
      setLogTarget(fu);
    }
  }

  if (loading) return <div className="empty-state"><div className="spinner" /></div>;

  const tiers = data?.tiers || { red: [], orange: [], blue: [] };
  const counts = data?.counts || {};

  return (
    <div>
      {/* Summary bar */}
      <div style={styles.summaryBar}>
        <span>📅 <strong style={{ color: 'var(--text)' }}>{counts.total || 0}</strong> follow-ups scheduled</span>
        {counts.overdue > 0 && <span>⚠️ <strong style={{ color: '#ef4444' }}>{counts.overdue}</strong> overdue</span>}
        {counts.today > 0 && <span>🔴 <strong style={{ color: '#ef4444' }}>{counts.today}</strong> due today</span>}
        {counts.tomorrow > 0 && <span>🟠 <strong style={{ color: '#f97316' }}>{counts.tomorrow}</strong> tomorrow</span>}
        {counts.thisWeek > 0 && <span>🔵 <strong style={{ color: '#60a5fa' }}>{counts.thisWeek}</strong> this week</span>}
      </div>

      {counts.total === 0 && (
        <div className="empty-state">
          <div className="empty-state-icon">📅</div>
          <div className="empty-state-text">No follow-ups scheduled.</div>
        </div>
      )}

      {/* Red — Due today / overdue */}
      {tiers.red.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, paddingBottom: 6, borderBottom: '1px solid #ef444440' }}>
            <span style={{ fontWeight: 700, fontSize: 13, color: '#ef4444', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              🔴 Due Today
            </span>
            <span style={{ background: '#ef4444', color: '#000', borderRadius: 10, padding: '1px 8px', fontSize: 11, fontWeight: 700 }}>
              {tiers.red.length}
            </span>
          </div>
          {tiers.red.map(fu => (
            <FollowUpCard
              key={fu.id}
              fu={fu}
              onLog={() => handleLog(fu)}
              onReschedule={() => setReschedTarget(fu)}
              onDismiss={() => handleDismiss(fu)}
              onView={() => onSelectProspect && onSelectProspect(fu.id, fu.pipeline)}
            />
          ))}
        </div>
      )}

      {/* Orange — Tomorrow */}
      {tiers.orange.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, paddingBottom: 6, borderBottom: '1px solid #f9731640' }}>
            <span style={{ fontWeight: 700, fontSize: 13, color: '#f97316', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              🟠 Tomorrow
            </span>
            <span style={{ background: '#f97316', color: '#000', borderRadius: 10, padding: '1px 8px', fontSize: 11, fontWeight: 700 }}>
              {tiers.orange.length}
            </span>
          </div>
          {tiers.orange.map(fu => (
            <FollowUpCard
              key={fu.id}
              fu={fu}
              onLog={() => handleLog(fu)}
              onReschedule={() => setReschedTarget(fu)}
              onDismiss={() => handleDismiss(fu)}
              onView={() => onSelectProspect && onSelectProspect(fu.id, fu.pipeline)}
            />
          ))}
        </div>
      )}

      {/* Blue — This week */}
      {tiers.blue.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, paddingBottom: 6, borderBottom: '1px solid #60a5fa40' }}>
            <span style={{ fontWeight: 700, fontSize: 13, color: '#60a5fa', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              🔵 This Week (3–7 days)
            </span>
            <span style={{ background: '#60a5fa', color: '#000', borderRadius: 10, padding: '1px 8px', fontSize: 11, fontWeight: 700 }}>
              {tiers.blue.length}
            </span>
          </div>
          {tiers.blue.map(fu => (
            <FollowUpCard
              key={fu.id}
              fu={fu}
              onLog={() => handleLog(fu)}
              onReschedule={() => setReschedTarget(fu)}
              onDismiss={() => handleDismiss(fu)}
              onView={() => onSelectProspect && onSelectProspect(fu.id, fu.pipeline)}
            />
          ))}
        </div>
      )}

      {/* Log Contact modal */}
      {logTarget && (
        logTarget.pipeline === 'pb101' ? (
          <PB101OutcomeModal
            record={{
              id: logTarget.id,
              name: logTarget.name,
              phone: logTarget.phone,
              skillLevel: logTarget.skillLevel,
              sessionDate: logTarget.sessionDate,
              clubId: logTarget.location_id,
              trialOfferedAgain: logTarget.trialOfferedAgain,
              attempt: 0,
            }}
            onClose={() => setLogTarget(null)}
            onSaved={() => { setLogTarget(null); load(); }}
          />
        ) : (
          <LeadLogContactModal
            prospect={logTarget}
            onClose={() => setLogTarget(null)}
            onSaved={() => { setLogTarget(null); load(); }}
          />
        )
      )}

      {/* Reschedule modal — reuses log contact modal pre-filled */}
      {reschedTarget && (
        reschedTarget.pipeline === 'pb101' ? (
          <PB101OutcomeModal
            record={{
              id: reschedTarget.id,
              name: reschedTarget.name,
              phone: reschedTarget.phone,
              skillLevel: reschedTarget.skillLevel,
              sessionDate: reschedTarget.sessionDate,
              clubId: reschedTarget.location_id,
              trialOfferedAgain: reschedTarget.trialOfferedAgain,
              attempt: 0,
            }}
            onClose={() => setReschedTarget(null)}
            onSaved={() => { setReschedTarget(null); load(); }}
          />
        ) : (
          <LeadLogContactModal
            prospect={reschedTarget}
            onClose={() => setReschedTarget(null)}
            onSaved={() => { setReschedTarget(null); load(); }}
          />
        )
      )}
    </div>
  );
}

// ─── Contact Log Edit Modal ───────────────────────────────────────────────────
function ContactLogEditModal({ log, onClose, onSaved }) {
  const { post } = useApi();
  const [form, setForm] = useState({
    outcome:       log.outcome       || '',
    contactMethod: log.contactMethod || '',
    notes:         log.notes         || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState(null);

  const inputStyle = {
    width: '100%', background: 'var(--surface-3)', border: '1px solid var(--border)',
    borderRadius: 6, color: 'var(--text)', padding: '8px 10px', fontSize: 13,
    boxSizing: 'border-box',
  };

  async function handleSave() {
    setSaving(true); setError(null);
    try {
      await post(`/lead/contact-log/${log.logId}`, {
        outcome:       form.outcome       || null,
        contactMethod: form.contactMethod || null,
        notes:         form.notes         || null,
      });
      onSaved({ ...log, outcome: form.outcome, contactMethod: form.contactMethod, notes: form.notes });
    } catch (err) {
      setError(err.message || 'Failed to save');
      setSaving(false);
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 480, width: '100%' }}>
        <div className="modal-title">✎ Edit Contact Log — {log.fullName}</div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 16 }}>
          {log.locationId} · {log.module?.toUpperCase()} · {new Date(log.loggedAt).toLocaleDateString()}
        </div>

        {error && (
          <div style={{ padding: '8px 12px', background: 'rgba(239,68,68,0.1)', border: '1px solid var(--red)', borderRadius: 6, fontSize: 13, color: 'var(--red)', marginBottom: 16 }}>
            {error}
          </div>
        )}

        <div className="field-group">
          <label className="field-label">Outcome</label>
          <select style={inputStyle} value={form.outcome} onChange={e => setForm(p => ({ ...p, outcome: e.target.value }))}>
            <option value="">— select —</option>
            {OUTCOME_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
          </select>
        </div>

        <div className="field-group">
          <label className="field-label">Contact Method</label>
          <select style={inputStyle} value={form.contactMethod} onChange={e => setForm(p => ({ ...p, contactMethod: e.target.value }))}>
            <option value="">— select —</option>
            <option value="call">Call</option>
            <option value="text">Text</option>
            <option value="email">Email</option>
            <option value="in-person">In Person</option>
          </select>
        </div>

        <div className="field-group">
          <label className="field-label">Notes</label>
          <textarea
            style={{ ...inputStyle, minHeight: 100, resize: 'vertical' }}
            value={form.notes}
            onChange={e => setForm(p => ({ ...p, notes: e.target.value }))}
            placeholder="Add context for the next rep..."
          />
        </div>

        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : '✓ Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Contact Log Tab ──────────────────────────────────────────────────────────
const MODULE_LABELS = { lead: 'Lead Engine', t2m: 'T2M', pb101: 'PB101' };
const MODULE_COLORS = { lead: '#f59e0b', t2m: '#22c55e', pb101: '#3b82f6' };

const OUTCOME_OPTIONS = [
  'Left Voicemail','Spoke With — Interested','Spoke With — Soft No',
  'Spoke With — Hard No','No Answer','Text Sent','Email Sent',
  'Converted','Re-Engage','Sold Trial','Sold Membership','Engaged','Follow-Up Needed','No Response','Nurture','Cool Period','Not Interested — Cool Period','Do Not Contact','Not Interested — Do Not Contact','Closed','Called + Email','Email Only','Call Only','Contact Made',
];

export function LeadContactLog() {
  const { get, post } = useApi();
  const { currentClub } = useClub();
  const [logs, setLogs]           = useState([]);
  const [loading, setLoading]     = useState(true);
  const [filters, setFilters]     = useState({ club: '', startDate: '', endDate: '', outcome: '', module: '' });
  const [expanded, setExpanded]   = useState(null);
  const [editTarget, setEditTarget] = useState(null);

  const ALL_CLUBS = ['WA','MF','GB','DEN','LEW','MCK'];

  function setF(k, v) { setFilters(p => ({ ...p, [k]: v })); }

  const load = useCallback(() => {
    setLoading(true);
    const p = new URLSearchParams();
    const club = filters.club || null;
    if (club)              p.set('club',      club);
    if (filters.startDate) p.set('startDate', filters.startDate);
    if (filters.endDate)   p.set('endDate',   filters.endDate);
    if (filters.outcome)   p.set('outcome',   filters.outcome);
    if (filters.module)    p.set('module',    filters.module);
    get(`/lead/contact-log?${p.toString()}`)
      .then(d => setLogs(d.logs || []))
      .catch(() => setLogs([]))
      .finally(() => setLoading(false));
  }, [filters, currentClub]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  function fmt(iso) {
    if (!iso) return '—';
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
      + ' · ' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  }

  const inputStyle = {
    background: 'var(--surface-2)', border: '1px solid var(--border)',
    borderRadius: 6, color: 'var(--text)', padding: '6px 10px', fontSize: 12,
  };

  return (
    <div style={{ padding: '0 0 40px' }}>

      {/* Edit Modal */}
      {editTarget && (
        <ContactLogEditModal
          log={editTarget}
          onClose={() => setEditTarget(null)}
          onSaved={(updated) => {
            setLogs(prev => prev.map(l => l.logId === updated.logId ? { ...l, ...updated } : l));
            setEditTarget(null);
          }}
        />
      )}
      {/* KPI Strip */}
      {!loading && logs.length > 0 && (() => {
        const total = logs.length;
        const spokeWith = logs.filter(l => l.outcome?.startsWith('Spoke With')).length;
        const contactRate = Math.round((spokeWith / total) * 100);
        const methodCounts = logs.reduce((acc, l) => { const m = l.contactMethod || 'unknown'; acc[m] = (acc[m] || 0) + 1; return acc; }, {});
        const topMethod = Object.entries(methodCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || '—';
        const memberAttempts = logs.reduce((acc, l) => { if (l.memberId) acc[l.memberId] = (acc[l.memberId] || 0) + 1; return acc; }, {});
        const avgAttempts = Object.keys(memberAttempts).length > 0 ? (Object.values(memberAttempts).reduce((a, b) => a + b, 0) / Object.keys(memberAttempts).length).toFixed(1) : '—';
        const methodPct = Object.entries(methodCounts).sort((a, b) => b[1] - a[1]).slice(0, 3).map(([m, c]) => ({ method: m, pct: Math.round((c / total) * 100) }));
        const methodColors = { call: '#f59e0b', text: '#3b82f6', email: '#8b5cf6', 'in-person': '#22c55e', unknown: '#888' };
        const card = { background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 14px' };
        return (
          <div style={{ marginBottom: 16 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: 10 }}>
              {[
                { num: total, label: 'Total Contacts', sub: null, color: 'var(--text)' },
                { num: `${contactRate}%`, label: 'Contact Rate', sub: 'Spoke With / attempts', color: contactRate >= 30 ? '#22c55e' : '#f59e0b' },
                { num: topMethod, label: 'Top Method', sub: null, color: 'var(--text)' },
                { num: avgAttempts, label: 'Avg Attempts', sub: 'per prospect', color: 'var(--text)' },
              ].map(k => (
                <div key={k.label} style={card}>
                  <div style={{ fontSize: 22, fontWeight: 700, color: k.color, fontFamily: 'DM Mono, monospace', textTransform: 'capitalize' }}>{k.num}</div>
                  <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 1, marginTop: 2 }}>{k.label}</div>
                  {k.sub && <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 3 }}>{k.sub}</div>}
                </div>
              ))}
            </div>
            {methodPct.length > 0 && (
              <div style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 14px', display: 'grid', gridTemplateColumns: `repeat(${methodPct.length}, 1fr)`, gap: 16 }}>
                {methodPct.map(({ method, pct }) => (
                  <div key={method}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>
                      <span style={{ textTransform: 'capitalize' }}>{method}</span>
                      <span style={{ fontWeight: 600, color: 'var(--text)' }}>{pct}%</span>
                    </div>
                    <div style={{ height: 5, borderRadius: 3, background: 'var(--border)', overflow: 'hidden' }}>
                      <div style={{ height: '100%', borderRadius: 3, width: `${pct}%`, background: methodColors[method] || '#888' }} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })()}

      {/* Filters */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16, alignItems: 'center' }}>
        <select style={inputStyle} value={filters.club} onChange={e => setF('club', e.target.value)}>
          <option value="">All Clubs</option>
          {ALL_CLUBS.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <select style={inputStyle} value={filters.module} onChange={e => setF('module', e.target.value)}>
          <option value="">All Engines</option>
          <option value="lead">Lead Engine</option>
          <option value="t2m">T2M</option>
          <option value="pb101">PB101</option>
        </select>
        <select style={inputStyle} value={filters.outcome} onChange={e => setF('outcome', e.target.value)}>
          <option value="">All Outcomes</option>
          {OUTCOME_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
        <input type="date" style={inputStyle} value={filters.startDate} onChange={e => setF('startDate', e.target.value)} />
        <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>to</span>
        <input type="date" style={inputStyle} value={filters.endDate} onChange={e => setF('endDate', e.target.value)} />
        {(filters.club || filters.module || filters.outcome || filters.startDate || filters.endDate) && (
          <button onClick={() => setFilters({ club:'', startDate:'', endDate:'', outcome:'', module:'' })}
            style={{ ...inputStyle, color: 'var(--text-muted)', cursor: 'pointer' }}>
            ✕ Clear
          </button>
        )}
        <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--text-muted)' }}>
          {loading ? 'Loading...' : `${logs.length} contact${logs.length !== 1 ? 's' : ''}`}
        </span>
      </div>

      {/* Table */}
      {loading ? (
        <div style={{ padding: 40, textAlign: 'center' }}><div className="spinner" /></div>
      ) : logs.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">📋</div>
          <div className="empty-state-text">No contacts logged matching these filters.</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {/* Header row */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '180px 80px 80px 160px 120px 1fr 100px 60px',
            gap: 8, padding: '6px 12px',
            fontSize: 10, fontWeight: 700, color: 'var(--text-muted)',
            textTransform: 'uppercase', letterSpacing: 1,
            borderBottom: '1px solid var(--border)',
          }}>
            <span>Prospect</span>
            <span>Club</span>
            <span>Engine</span>
            <span>Outcome</span>
            <span>Method</span>
            <span>Notes</span>
            <span>Date</span>
            <span></span>
          </div>

          {logs.map(log => (
            <div key={log.logId}
              onClick={() => setExpanded(expanded === log.logId ? null : log.logId)}
              style={{
                display: 'grid',
                gridTemplateColumns: '180px 80px 80px 160px 120px 1fr 100px 60px',
                gap: 8, padding: '10px 12px',
                background: expanded === log.logId ? 'var(--surface-3)' : 'var(--surface-2)',
                border: '1px solid var(--border)',
                borderRadius: 6, cursor: 'pointer', fontSize: 12,
                alignItems: 'center',
              }}
            >
              {/* Prospect */}
              <div>
                <div style={{ fontWeight: 600, color: 'var(--text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {log.fullName}
                </div>
                <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}>
                  by {log.loggedBy || '—'}
                </div>
              </div>

              {/* Club */}
              <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)' }}>
                {log.locationId}
              </span>

              {/* Engine */}
              <span style={{
                fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 4,
                background: (MODULE_COLORS[log.module] || '#888') + '22',
                color: MODULE_COLORS[log.module] || '#888',
                whiteSpace: 'nowrap',
              }}>
                {MODULE_LABELS[log.module] || log.module}
              </span>

              {/* Outcome */}
              <span style={{ fontSize: 11, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {log.outcome || '—'}
              </span>

              {/* Method */}
              <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                {log.contactMethod || '—'}
              </span>

              {/* Notes */}
              <span style={{ fontSize: 11, color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {expanded === log.logId ? (log.notes || '—') : (log.notes ? log.notes.slice(0, 60) + (log.notes.length > 60 ? '…' : '') : '—')}
              </span>

              {/* Date */}
              <span style={{ fontSize: 10, color: 'var(--text-muted)', textAlign: 'right' }}>
                {fmt(log.loggedAt)}
              </span>

              {/* Edit */}
              <button
                onClick={e => { e.stopPropagation(); setEditTarget(log); }}
                style={{
                  background: 'transparent', border: '1px solid var(--border)',
                  borderRadius: 5, color: 'var(--text-muted)', fontSize: 11,
                  padding: '3px 8px', cursor: 'pointer',
                }}
              >
                ✎ Edit
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
